import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { GraduationCap, Radar } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { Panel, Skeleton, MiniEmpty, CHART } from "./primitives";

const PROGRAM_FILTER = { retail_traditional: { candidate_type: "retail_traditional" }, retail_acquisition: { candidate_type: "retail_acquisition" }, associate: { candidate_type: "associate_agent" }, protege: { candidate_type: "protege" } };

export function ProgramProgress({ data, loading, drill, index }) {
  const reduce = useReducedMotion();
  const rows = data?.program_progress || [];
  return (
    <Panel title="Program Progress" icon={GraduationCap} testId="program-progress" className="lg:col-span-7" index={index}>
      {loading && !data ? <Skeleton h="h-40" /> : (rows.length ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map((p) => (
            <button key={p.key} data-testid={`program-${p.key}`} onClick={() => drill({ tab: "all", ...(PROGRAM_FILTER[p.key] || {}) })}
              className="text-left rounded-lg border border-slate-200 p-3.5 hover:border-brand/40 hover:shadow-sm transition-all duration-200 hover:-translate-y-0.5">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-slate-800 text-sm">{p.name}</p>
                <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{p.active} active</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-2">
                <motion.div className="h-full rounded-full bg-brand" initial={reduce ? false : { width: 0 }} animate={{ width: `${p.avg_completion ?? 0}%` }} transition={{ duration: 0.7, ease: "easeOut" }} />
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                <span className="text-slate-500 font-medium">{p.avg_completion == null ? "— avg" : `${p.avg_completion}% avg`}</span>
                {p.blocked > 0 && <span className="text-rose-600">{p.blocked} blocked</span>}
                {p.ready_for_review > 0 && <span className="text-emerald-600">{p.ready_for_review} ready for review</span>}
                {p.active === 0 && p.avg_completion == null && p.ready_for_review === 0 && <span className="text-slate-300">No active enrollments</span>}
              </div>
            </button>
          ))}
        </div>
      ) : <MiniEmpty icon={GraduationCap} title="No program entries" description="Program progress will appear here." testId="program-empty" />)}
    </Panel>
  );
}

function SrcTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return <div className="rounded-lg border border-slate-200 bg-white shadow-lg px-3 py-1.5 text-xs"><span className="font-semibold text-slate-700">{d.source}</span><span className="text-slate-500"> · {d.count} candidate{d.count === 1 ? "" : "s"}{d.pct ? ` · ${d.pct}%` : ""}</span></div>;
}

export function SourceChart({ data, loading, drill, index }) {
  const reduce = useReducedMotion();
  const rows = data?.source_effectiveness || [];
  const h = Math.max(96, rows.length * 38);   // compact for 1-2 sources; grows with data
  return (
    <Panel title="Source Effectiveness" icon={Radar} testId="source-effectiveness" index={index}>
      {loading && !data ? <Skeleton h="h-40" /> : (rows.length ? (
        <div style={{ height: h }} className="w-full" data-testid="source-chart-body">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }} barCategoryGap={10}>
              <XAxis type="number" hide allowDecimals={false} />
              <YAxis type="category" dataKey="source" width={110} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip content={<SrcTooltip />} cursor={{ fill: "rgba(0,96,144,0.04)" }} />
              <Bar dataKey="count" radius={[0, 5, 5, 0]} isAnimationActive={!reduce} animationDuration={700} cursor="pointer"
                onClick={(d) => d && drill({ tab: "all", source: d.source })} label={{ position: "right", fontSize: 11, fill: "#94a3b8" }}>
                {rows.map((_, i) => <Cell key={i} fill={i === 0 ? CHART.blue : CHART.blueLt} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : <MiniEmpty icon={Radar} title="No source data" description="No candidates recorded in this date range." testId="source-empty" />)}
    </Panel>
  );
}
